import { FC, useCallback, useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { IMedicationInOutGridProps } from './MedicationInOutGrid.interface';

import './style.scss';

const NotAllowedCell = () => (
  <TableCell
    className="in-out-grid__competition-cell
               in-out-grid__not-allowed-bg"
  >
    <div className="in-out-grid__cell-container">
      <CloseIcon fontSize="large" />
      Not Allowed
    </div>
  </TableCell>
);

const AllowedCell = () => (
  <TableCell
    className="in-out-grid__competition-cell
               in-out-grid__allowed-bg"
  >
    <div className="in-out-grid__cell-container">
      <CheckIcon fontSize="large" />
      Allowed
    </div>
  </TableCell>
);

const ConditionalCell = () => (
  <TableCell
    className="in-out-grid__competition-cell
               in-out-grid__conditional-bg"
  >
    <div className="in-out-grid__cell-container">
      <WarningIcon fontSize="large" />
      Conditional
    </div>
  </TableCell>
);

const MedicationInOutGrid: FC<IMedicationInOutGridProps> = ({
  administrations,
  rules,
}): JSX.Element => {
  const [isTooltipVisible, setTooltipVisible] = useState(true);
  const componentRef = useRef<HTMLDivElement | null>(null);

  const onMouseEnter = useCallback(() => {
    setTooltipVisible(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setTooltipVisible(false);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <TableContainer ref={componentRef} className="in-out-grid">
      <Table>
        <TableHead className="in-out-grid__table-head">
          <TableRow>
            {!!administrations.length && <TableCell className="in-out-grid__cell" />}

            <TableCell className="in-out-grid__cell">In Competition</TableCell>
            <TableCell className="in-out-grid__cell">Out of Competition</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {administrations.length
            ? administrations.map(
                ({ allowedIn, allowedOutside, longText, name, shortText, condition }, index) => (
                  <TableRow key={index} onMouseEnter={onMouseEnter}>
                    <TableCell className="in-out-grid__rule">
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          isTooltipVisible &&
                          longText && <div className="in-out-grid__tooltip">{longText}</div>
                        }
                      >
                        <span>
                          <div className="in-out-grid__rule-name">{name}</div>
                          <div className="in-out-grid__rule-description">{shortText}</div>
                        </span>
                      </Tooltip>
                    </TableCell>
                    {condition ? (
                      <ConditionalCell />
                    ) : allowedIn ? (
                      <AllowedCell />
                    ) : (
                      <NotAllowedCell />
                    )}
                    {condition ? (
                      <ConditionalCell />
                    ) : allowedOutside ? (
                      <AllowedCell />
                    ) : (
                      <NotAllowedCell />
                    )}
                  </TableRow>
                ),
              )
            : rules?.map((rule, index) => (
                <TableRow key={index}>
                  {rule.allowedIn ? <AllowedCell /> : <NotAllowedCell />}
                  {rule.allowedOutside ? <AllowedCell /> : <NotAllowedCell />}
                </TableRow>
              ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MedicationInOutGrid;
